import React, { useMemo, useState } from 'react';
import { Breadcrumbs, H6 } from '../../../AbstractElements';
import DataTable from 'react-data-table-component';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Input,
  Badge,
} from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import InvoiceService from '../../../Services/invoices/InvoiceService';
import LabelService from '../../../Services/label/LabelService';
import SpinnerLoader from '../../Loaders/Spinner';

function InvoicesTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('All');
  const [selectedLabelId, setSelectedLabelId] = useState('');

  const { data: labelsResult, isLoading: labelsLoading } = useQuery({
    queryKey: ['accounting-labels'],
    queryFn: LabelService.getLabels,
  });

  const labelOptions = useMemo(() => {
    const rows = labelsResult?.data || [];
    return rows.map((label) => ({
      value: String(label.id),
      label: label.label_name || label.name || `Label ${label.id}`,
      redId: label.label_red_id,
    }));
  }, [labelsResult]);

  const {
    data: invoicesResult,
    isLoading: invoicesLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['label-invoices', selectedLabelId],
    queryFn: () => InvoiceService.getLabelInvoices(selectedLabelId),
    enabled: !!selectedLabelId,
  });

  const invoices = useMemo(() => {
    const rows = invoicesResult?.data || [];
    return rows.map((inv) => ({
      id: inv.id,
      uuid: inv.uuid,
      reference: inv.reference || inv.invoice_number || inv.id,
      month_year: inv.month_year,
      status: inv.status || (inv.paid ? 'Paid' : 'Unpaid'),
      visibility: inv.visibility,
      artist_name: inv.artist?.artist_name || inv.artist?.full_name || inv.artist_name || '—',
      label_id: inv.label_id,
      file_path: inv.file_path,
      created_at: inv.created_at,
      updated_at: inv.updated_at,
    }));
  }, [invoicesResult]);

  const filteredData = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return invoices.filter((row) => {
      const statusOk =
        filter === 'All' ||
        (filter === 'Paid' && String(row.status).toLowerCase() === 'paid') ||
        (filter === 'Unpaid' && String(row.status).toLowerCase() !== 'paid');
      if (!statusOk) return false;
      if (!q) return true;
      return (
        String(row.reference || '').toLowerCase().includes(q) ||
        String(row.artist_name || '').toLowerCase().includes(q) ||
        String(row.month_year || '').toLowerCase().includes(q)
      );
    });
  }, [invoices, searchTerm, filter]);

  const tableColumns = [
    {
      name: 'Reference',
      selector: (row) => row.reference,
      sortable: true,
    },
    {
      name: 'Period',
      selector: (row) => row.month_year || '—',
      sortable: true,
    },
    {
      name: 'Artist',
      selector: (row) => row.artist_name,
      sortable: true,
    },
    {
      name: 'Status',
      cell: (row) => (
        <Badge color={String(row.status).toLowerCase() === 'paid' ? 'success' : 'warning'}>
          {row.status || '—'}
        </Badge>
      ),
      sortable: true,
    },
    {
      name: 'Visible',
      selector: (row) => (row.visibility ? 'Yes' : 'No'),
    },
    {
      name: 'Updated',
      selector: (row) => (row.updated_at ? String(row.updated_at).slice(0, 10) : '—'),
      sortable: true,
    },
  ];

  if (labelsLoading) return <SpinnerLoader />;

  return (
    <>
      <Breadcrumbs parent="Invoices" title="Invoices" mainTitle="Invoices List" />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <h5 className="mb-0">Invoices by releasing label</h5>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <Input
                      type="select"
                      value={selectedLabelId}
                      onChange={(e) => setSelectedLabelId(e.target.value)}
                      className="rd-fluid-search"
                      style={{ minWidth: 0, maxWidth: '16rem' }}
                    >
                      <option value="">Select label…</option>
                      {labelOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                    <Input
                      type="text"
                      placeholder="Search reference, artist, period"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="rd-fluid-search"
                      style={{ minWidth: 0, maxWidth: '16rem' }}
                      disabled={!selectedLabelId}
                    />
                    <Input
                      type="select"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      style={{ width: '120px' }}
                      disabled={!selectedLabelId}
                    >
                      <option value="All">All</option>
                      <option value="Paid">Paid</option>
                      <option value="Unpaid">Unpaid</option>
                    </Input>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                {!selectedLabelId ? (
                  <H6 attrH6={{ className: 'text-muted mb-0' }}>
                    Choose a label to load invoices scoped by invoice.label_id (releasing label).
                  </H6>
                ) : invoicesLoading ? (
                  <SpinnerLoader />
                ) : isError ? (
                  <div>Failed to load invoices: {error?.message || 'Unknown error'}</div>
                ) : (
                  <div className="invoices-table-wrap">
                    <DataTable
                      columns={tableColumns}
                      data={filteredData}
                      pagination
                      highlightOnHover
                      responsive
                      noDataComponent="No invoices for this label."
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default InvoicesTable;
